import { create } from "zustand";
import { immer } from "zustand/middleware/immer";

import { Network, networksArray } from "@/interfaces/network";
import { AirdropProps } from "@/interfaces/store";
import { AirdropResponse } from "@/services/rpc/near";
import { createObjectFromKeys, toMap } from "@/utils";
import { formatAirdrop } from "@/utils/airdrop";

import { useStore } from "./useStore";

type Airdrops = Record<number, AirdropProps>;

interface Store {
  airdrops: Record<Network, Airdrops>;
  hapiBalance: null | string;
  setHapiBalance: (balance: string | null) => void;
  setAirdrops: (network: Network, airdrops: Array<AirdropResponse>, minAllowedScore: number) => void;
  setAirdropsById: (network: Network, airdrop: AirdropResponse) => void;
}

export const useAirdrops = create<Store>()(
  immer((set) => ({
    airdrops: createObjectFromKeys(networksArray, {} as Airdrops),
    hapiBalance: null,
    setHapiBalance: (balance: string | null) =>
      set((state) => {
        state.hapiBalance = balance;
      }),

    setAirdrops: (network, airdrops, minAllowedScore) =>
      set((state) => {
        const nfts = useStore.getState().user.networksData?.[network]?.nft?.array;
        const hapiBalance = useAirdrops.getState().hapiBalance;
        const formattedAirdrops: Array<AirdropProps> = airdrops.map((airdrop) =>
          formatAirdrop(airdrop, nfts, minAllowedScore, hapiBalance),
        );
        state.airdrops = { ...state.airdrops, [network]: toMap(formattedAirdrops, "id") };
      }),
    setAirdropsById: (network, airdrop) =>
      set((state) => {
        const airdropsByNetwork = state.airdrops[network];
        const airdropsById = airdropsByNetwork[airdrop.id];
        const nfts = airdropsById.nfts || undefined;
        const hapiBalance = useAirdrops.getState().hapiBalance;
        const formattedAirdrop = formatAirdrop(airdrop, nfts, airdropsById.minAllowedScore, hapiBalance);

        state.airdrops = {
          ...state.airdrops,
          [network]: {
            ...airdropsByNetwork,
            [airdrop.id]: {
              ...formattedAirdrop,
              available_nft_ids: airdropsById.available_nft_ids,
            },
          },
        };
      }),
  })),
);
